import React from 'react';

import classNames from 'classnames';

import ProjectDashboardEmptyState from './ProjectDashboardEmptyState';

import { decimalUpTo } from 'Helpers/currencyFormat';

import LoadingSpinner from 'CommonComponents/LoadingSpinner';

// The four exec tiles, in display order. Value colours are taken from the wireframe:
// overdue in `tw-text-red` (#F00024, already an exact match) and at risk in the
// wireframe's dark goldenrod, which no palette token carries — `amber-700` (#b45309)
// is noticeably more orange. Full literal class strings so Tailwind's content scanner
// keeps them in the build.
// `Key milestones due` names the window the key-milestones card below is set to and
// follows it; the other three tiles carry no window. "At risk" deliberately does not —
// it counts every slipping key milestone across the whole plan.
const TILES = [
    { key: 'plan_complete_percentage', label: 'Plan complete', suffix: '%', value: 'tw-text-charcoal' },
    { key: 'key_milestones_due_count', label: 'Key milestones due ({{n}} days)', windowed: true, value: 'tw-text-charcoal' },
    { key: 'overdue_activities_count', label: 'Overdue activities', value: 'tw-text-red' },
    { key: 'key_milestones_at_risk_count', label: 'Key milestones at risk', value: 'tw-text-[#b8860b]' },
];

// `decimalUpTo` rather than `toLocaleString()`: the latter follows the viewer's own
// browser locale, so two people looking at the same plan would see different figures.
// Matches how the Activities-by-owner card below formats its aggregate counts.
const tileValue = (summary, tile) => `${decimalUpTo(summary[tile.key] || 0)}${tile.suffix || ''}`;

export default function ProjectDashboardExecutiveSummary(props) {
    const summary = props.data || {};

    // Only a missing payload means "no plan yet" — the backend omits the summary when
    // the event has no projects at all. Four zeros are NOT empty: a plan whose work is
    // all still ahead of it genuinely reads 0% complete with nothing overdue, and
    // hiding that would make the strip disagree with the cards below it.
    const isEmpty = !props.data;

    return (
        <div className="table__card">
            <div className="tw-mb-6">
                <h4 className="table__header">{$t('Executive summary')}</h4>
            </div>

            {props.loading ? (
                <LoadingSpinner />
            ) : isEmpty ? (
                // No CTAs here on purpose. The strip only empties when the event has no
                // projects at all — the same moment the cards below empty and offer the
                // very same two buttons, so repeating them would stack three identical
                // pairs down the page. The key milestones panel keeps its CTAs because it
                // empties independently, on an event whose plan is otherwise full.
                <ProjectDashboardEmptyState
                    headline={$t('No activities yet')}
                    supporting={$t('Add activities to build your project plan and track progress against dates, owners and status')}
                />
            ) : (
                /* Tile geometry is the wireframe's `.metric`: 12px grid gap, 10px radius,
                   14px/16px padding, 12px label, 26px/600 value with a 4px gap. The nearest
                   scale steps (rounded-lg, py-3, text-2.5xl) are each 2px out, so the exact
                   values are set here. Label and value colours already resolve to the
                   wireframe's #8D8E8E and #1B1C1D via charcoal-50 / charcoal. */
                <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-4 tw-gap-3">
                    {TILES.map((tile) => (
                        <div key={`exec-${tile.key}`} className="tw-rounded-[10px] tw-bg-charcoal-10 tw-px-4 tw-py-3.5">
                            <div className="tw-text-xs tw-text-charcoal-50">{tile.windowed ? $t(tile.label, { n: props.windowDays }) : $t(tile.label)}</div>
                            <div className={classNames('tw-text-[26px] tw-font-semibold tw-mt-1 tw-leading-8', tile.value)}>{tileValue(summary, tile)}</div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
